/**
 * Ordenação da listagem: o contrato usa `sort: [GeoFenceSortField!]` e
 * `sortDirection: [SortDirection!]` como ARRAYS PAREADOS (índice a índice).
 * A UI escolhe um campo + direção; aqui convertemos para o par de arrays.
 *
 * ATENÇÃO (MK-21222): a listagem NÃO usa isto hoje, e é de propósito. Mandar sort **substitui** o
 * default do `geofence-microservice` inteiro, e é o default que carrega a regra de negócio
 * (`DEFAULT_GEO_FENCE_LIST_SORT = { draft: -1, 'cachedMetrics.entries': -1, _id: -1 }`, aplicado só
 * quando o input vem sem sort). Este helper fica de pé para quando existir uma UI de ordenar; quem
 * ligar de volta no `buildListInput` precisa saber que está desligando a prioridade de Rascunho.
 */
export const SORT_FIELDS = {
  NAME: 'NAME',
  ENTRIES: 'ENTRIES',
  CREATED_AT: 'CREATED_AT',
}

export const SORT_DIRECTIONS = {
  ASC: 'ASC',
  DESC: 'DESC',
}

/**
 * @param {string} field  um de SORT_FIELDS (ou vazio/nulo = sem ordenação explícita)
 * @param {string} direction  ASC|DESC (default DESC)
 * @returns {{ sort: string[], sortDirection: string[] }} arrays pareados p/ ListGeoFencesInput
 */
export const buildSortInput = (field, direction = SORT_DIRECTIONS.DESC) => {
  if (!field || !SORT_FIELDS[field]) return { sort: [], sortDirection: [] }
  const dir = SORT_DIRECTIONS[direction] ? direction : SORT_DIRECTIONS.DESC
  return { sort: [field], sortDirection: [dir] }
}

/**
 * Ordem dos grupos de status na listagem (RFC-195), aplicada client-side e de forma ESTÁVEL.
 *
 * O agrupamento continua aqui porque o default do backend garante Rascunho antes do resto
 * (`draft: -1`), mas não separa Inativa de Expirada — os dois são `draft: false` e a distinção é
 * derivada de `inactivatedAt`. Dentro de cada grupo, a ordem é a que o backend entregou
 * (entradas DESC, e depois `_id` DESC), e preservá-la é parte do contrato: reordenar aqui
 * desfaria a regra de negócio que o serviço acabou de aplicar.
 */
export const STATUS_GROUP_ORDER = ['DRAFT', 'ACTIVE', 'INACTIVE', 'EXPIRED']

/**
 * Agrupa por status na ordem Rascunho > Ativa > Inativa > Expirada, mantendo a ordem
 * relativa de entrada dentro de cada grupo (ou seja, ENTRIES DESC vindo do backend).
 *
 * Status desconhecido vai para o fim, também estável: sumir com uma cerca da listagem
 * porque o backend passou a mandar um status novo seria pior que exibi-la fora de ordem.
 *
 * @param {Array<{status: string}>} fences
 * @returns {Array} nova lista; o argumento não é mutado
 */
export const orderByStatusGroup = (fences) => {
  if (!Array.isArray(fences)) return []
  const buckets = new Map(STATUS_GROUP_ORDER.map((s) => [s, []]))
  const desconhecidos = []
  fences.forEach((f) => {
    const bucket = buckets.get(f?.status)
    if (bucket) bucket.push(f)
    else desconhecidos.push(f)
  })
  return [...STATUS_GROUP_ORDER.flatMap((s) => buckets.get(s)), ...desconhecidos]
}
